import "@emotion/react";
import styled from "@emotion/styled";
import css from "@styled-system/css";
import { createShouldForwardProp, props } from "@styled-system/should-forward-prop";
import React, { ComponentPropsWithoutRef, ReactNode, RefAttributes } from "react";
import * as StyledSystem from "styled-system";
import {
  background,
  border,
  color,
  compose,
  flexbox,
  grid,
  layout,
  position,
  shadow,
  space,
  typography,
} from "styled-system";
import extraConfig, { transformAliasProps as tx } from "./config";
import { ITheme } from "../theme/theme";
export const truncate = (props: any): any => {
  if (props.isTruncated) {
    return {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    };
  }

  return {};
};

export const systemProps = compose(
  layout,
  color,
  space,
  background,
  border,
  grid,
  position,
  shadow,
  typography,
  flexbox,
  extraConfig,
);

const shouldForwardProp = createShouldForwardProp([
  ...props,
  "d",
  "textDecoration",
  "pointerEvents",
  "visibility",
  "transform",
  "cursor",
  "fill",
  "stroke",
]);

/**
 * htmlWidth and htmlHeight is used in the <Image />
 * component to support the native `width` and `height` attributes
 */
const nativeHTMLPropAlias = ["htmlWidth", "htmlHeight"];

const hover = "&:hover";
const active = "&:active, &[data-active=true]";
const focus = "&:focus";
const visited = "&:visited";
const even = "&:nth-of-type(even)";
const odd = "&:nth-of-type(odd)";
const disabled =
  "&:disabled, &:disabled:focus, &:disabled:hover, &[aria-disabled=true], &[aria-disabled=true]:focus, &[aria-disabled=true]:hover";
const checked = "&[aria-checked=true]";
const mixed = "&[aria-checked=mixed]";
const selected = "&[aria-selected=true]";
const invalid = "&[aria-invalid=true]";
const pressed = "&[aria-pressed=true]";
const readOnly = "&[aria-readonly=true], &[readonly]";
const first = "&:first-of-type";
const last = "&:last-of-type";
const expanded = "&[aria-expanded=true]";
const grabbed = "&[aria-grabbed=true]";
const notFirst = "&:not(:first-of-type)";
const notLast = "&:not(:last-of-type)";
const groupHover = "[role=group]:hover &";

const Box = styled("div", {
  shouldForwardProp: prop => {
    if (nativeHTMLPropAlias.includes(prop.toString())) {
      return true;
    }

    return shouldForwardProp(prop.toString());
  },
})<StyledBoxProps & { color?: string }>(
  truncate,
  systemProps,
  ({
    _after,
    _focus,
    _selected,
    _focusWithin,
    _hover,
    _invalid,
    _active,
    _disabled,
    _grabbed,
    _pressed,
    _expanded,
    _visited,
    _before,
    _readOnly,
    _first,
    _notFirst,
    _notLast,
    _last,
    _placeholder,
    _checked,
    _groupHover,
    _mixed,
    _odd,
    _even,
  }) => {
    return css({
      [hover]: tx(_hover),
      [focus]: tx(_focus),
      [active]: tx(_active),
      [visited]: tx(_visited),
      [disabled]: tx(_disabled),
      [selected]: tx(_selected),
      [invalid]: tx(_invalid),
      [expanded]: tx(_expanded),
      [grabbed]: tx(_grabbed),
      [readOnly]: tx(_readOnly),
      [first]: tx(_first),
      [notFirst]: tx(_notFirst),
      [notLast]: tx(_notLast),
      [last]: tx(_last),
      [odd]: tx(_odd),
      [even]: tx(_even),
      [mixed]: tx(_mixed),
      [checked]: tx(_checked),
      [pressed]: tx(_pressed),
      [groupHover]: tx(_groupHover),
      "&:before": tx(_before),
      "&:after": tx(_after),
      "&:focus-within": tx(_focusWithin),
      "&::placeholder": tx(_placeholder),
    }) as any;
  },
);

export type BoxProps = ComponentPropsWithoutRef<typeof Box> & RefAttributes<any>;

interface IPseudoBoxProps {
  /**
   * Styles for CSS selector `&:after`
   *
   * NOTE:When using this, ensure the `content` is wrapped in a backtick.
   * @example
   * ```jsx
   * <Box _after={{content:`""` }}/>
   * ```
   */
  _after?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS selector `&:before`
   *
   * NOTE:When using this, ensure the `content` is wrapped in a backtick.
   * @example
   * ```jsx
   * <Box _before={{content:`""` }}/>
   * ```
   */
  _before?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS selector `&:focus`
   *
   */
  _focus?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS selector `&:hover`
   */
  _hover?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:active`
   */
  _active?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&[aria-pressed=true]`
   * Typically used to style the current "pressed" state of toggle buttons
   */
  _pressed?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles to apply when the ARIA attribute `aria-selected` is `true`
   * - CSS selector `&[aria-selected=true]`
   */
  _selected?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles to apply when a child of this element has received focus
   * - CSS Selector `&:focus-within`
   */
  _focusWithin?: StyledSystemProps & ModifiedStyledSystemProps;

  /**
   * Styles to apply when the ARIA attribute `aria-invalid` is `true`
   * - CSS selector `&[aria-invalid=true]`
   */
  _invalid?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles to apply when this element is disabled. The passed styles are applied to these CSS selectors:
   * - `&[aria-disabled=true]`
   * - `&:disabled`
   * - `&:disabled:focus`
   * - `&:disabled:hover`
   * - `&:focus[aria-disabled=true]`
   * - `&:hover[aria-disabled=true]`
   */
  _disabled?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles to apply when the ARIA attribute `aria-grabbed` is `true`
   * - CSS selector `&[aria-grabbed=true]`
   */
  _grabbed?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles to apply when the ARIA attribute `aria-expanded` is `true`
   * - CSS selector `&[aria-expanded=true]`
   */
  _expanded?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles to apply when the ARIA attribute `aria-checked` is `true`
   * - CSS selector `&[aria-checked=true]`
   */
  _checked?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles to apply when the ARIA attribute `aria-checked` is `mixed`
   * - CSS selector `&[aria-checked=mixed]`
   */
  _mixed?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:nth-child(odd)`
   */
  _odd?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:nth-child(even)`
   */
  _even?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:visited`
   */
  _visited?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:readonly`
   */
  _readOnly?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:first-of-type`
   */
  _first?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:last-of-type`
   */
  _last?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles to apply when you hover on a parent that has `role=group`.
   */
  _groupHover?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:not(:first-of-type)`
   */
  _notFirst?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&:not(:last-of-type)`
   */
  _notLast?: StyledSystemProps & ModifiedStyledSystemProps;
  /**
   * Styles for CSS Selector `&::placeholder`.
   * Useful for inputs
   */
  _placeholder?: StyledSystemProps & ModifiedStyledSystemProps;
}

export type StyledBoxProps = BoxHTMLProps &
  StyledSystemProps &
  ModifiedStyledSystemProps &
  IPseudoBoxProps &
  ITruncated & {
    as?: React.ElementType;
  } & { children?: ReactNode };

export default Box;

type CSS = React.CSSProperties;

type borderRadius = StyledSystem.ResponsiveValue<keyof ITheme["radii"] | number>;
type borderColor = StyledSystem.ResponsiveValue<StyledSystem.BorderColorProps["borderColor"]>;

interface ICustomConfig {
  // Custom borderRadius alias
  rounded?: borderRadius;
  roundedTop?: borderRadius;
  roundedBottom?: borderRadius;
  roundedLeft?: borderRadius;
  roundedRight?: borderRadius;
  roundedTopRight?: borderRadius;
  roundedTopLeft?: borderRadius;
  roundedBottomRight?: borderRadius;
  roundedBottomLeft?: borderRadius;

  // Custom borderColor alias
  borderBottomColor?: borderColor;
  borderTopColor?: borderColor;
  borderRightColor?: borderColor;
  borderLeftColor?: borderColor;

  // Custom width alias
  w?: StyledSystem.WidthProps["width"];
  minW?: StyledSystem.MinWidthProps["minWidth"];
  maxW?: StyledSystem.MaxWidthProps["maxWidth"];

  // Custom height alias
  h?: StyledSystem.HeightProps["height"];
  minH?: StyledSystem.MinHeightProps["minHeight"];
  maxH?: StyledSystem.MaxHeightProps["maxHeight"];

  // Custom display alias
  d?: StyledSystem.DisplayProps["display"];

  // Custom background alias
  backgroundAttachment?: StyledSystem.ResponsiveValue<CSS["backgroundAttachment"]>;
  bgImg?: StyledSystem.BackgroundImageProps["backgroundImage"];
  bgImage?: StyledSystem.BackgroundImageProps["backgroundImage"];
  bgSize?: StyledSystem.BackgroundSizeProps["backgroundSize"];
  bgPos?: StyledSystem.BackgroundPositionProps["backgroundPosition"];
  pos?: StyledSystem.PositionProps["position"];
  flexDir?: StyledSystem.FlexDirectionProps["flexDirection"];

  // CSS properties
  textDecoration?: StyledSystem.ResponsiveValue<CSS["textDecoration"]>;
  textDecor?: StyledSystem.ResponsiveValue<CSS["textDecoration"]>;
  textTransform?: StyledSystem.ResponsiveValue<CSS["textTransform"]>;
  overflowX?: StyledSystem.OverflowProps["overflow"];
  overflowY?: StyledSystem.OverflowProps["overflow"];
  appearance?: StyledSystem.ResponsiveValue<CSS["appearance"]>;
  transform?: StyledSystem.ResponsiveValue<CSS["transform"]>;
  transformOrigin?: StyledSystem.ResponsiveValue<CSS["transformOrigin"]>;
  animation?: StyledSystem.ResponsiveValue<CSS["animation"]>;
  userSelect?: StyledSystem.ResponsiveValue<CSS["userSelect"]>;
  pointerEvents?: StyledSystem.ResponsiveValue<CSS["pointerEvents"]>;
  boxSizing?: StyledSystem.ResponsiveValue<CSS["boxSizing"]>;
  cursor?: StyledSystem.ResponsiveValue<CSS["cursor"]>;
  resize?: StyledSystem.ResponsiveValue<CSS["resize"]>;
  transition?: StyledSystem.ResponsiveValue<CSS["transition"]>;
  objectFit?: StyledSystem.ResponsiveValue<CSS["objectFit"]>;
  objectPosition?: StyledSystem.ResponsiveValue<CSS["objectPosition"]>;
  visibility?: StyledSystem.ResponsiveValue<CSS["visibility"]>;

  // Ellipsis alias
  wordBreak?: StyledSystem.ResponsiveValue<CSS["wordBreak"]>;
  overflowWrap?: StyledSystem.ResponsiveValue<CSS["overflowWrap"]>;
  whiteSpace?: StyledSystem.ResponsiveValue<CSS["whiteSpace"]>;

  // SVG color properties
  fill?: StyledSystem.ColorProps["color"];
  stroke?: StyledSystem.ColorProps["color"];

  bgAttachment?: StyledSystem.ResponsiveValue<CSS["backgroundAttachment"]>;
  shadow?: StyledSystem.BoxShadowProps["boxShadow"];

  // List properties
  listStyleType?: StyledSystem.ResponsiveValue<CSS["listStyleType"]>;
  listStylePosition?: StyledSystem.ResponsiveValue<CSS["listStylePosition"]>;
  listStyleImage?: StyledSystem.ResponsiveValue<CSS["listStyleImage"]>;
  listStyleImg?: StyledSystem.ResponsiveValue<CSS["listStyleImage"]>;
  listStylePos?: StyledSystem.ResponsiveValue<CSS["listStylePosition"]>;

  // Outline prop
  outline?: StyledSystem.ResponsiveValue<CSS["outline"]>;
  float?: StyledSystem.ResponsiveValue<CSS["float"]>;
  willChange?: StyledSystem.ResponsiveValue<CSS["willChange"]>;

  // Border Width props
  borderTopWidth?: StyledSystem.ResponsiveValue<CSS["borderTopWidth"]>;
  borderBottomWidth?: StyledSystem.ResponsiveValue<CSS["borderBottomWidth"]>;
  borderLeftWidth?: StyledSystem.ResponsiveValue<CSS["borderLeftWidth"]>;
  borderRightWidth?: StyledSystem.ResponsiveValue<CSS["borderRightWidth"]>;
}

interface IFontSize {
  fontSize?: StyledSystem.ResponsiveValue<keyof ITheme["fontSizes"] | number | string>;
}

interface IFontWeight {
  fontWeight?: StyledSystem.ResponsiveValue<keyof ITheme["fontWeights"] | number>;
}

interface ILineHeight {
  lineHeight?: StyledSystem.ResponsiveValue<keyof ITheme["lineHeights"] | number | string>;
}

interface ILetterSpacing {
  letterSpacing?: StyledSystem.ResponsiveValue<keyof ITheme["lineHeights"] | number>;
}

type TypographyProps = Omit<StyledSystem.TypographyProps, "fontWeight" | "lineHeight" | "fontSize" | "letterSpacing">;

interface ITruncated {
  /**
   * If `true`, the text will be truncated
   */
  isTruncated?: boolean;
}

type StyledSystemProps = StyledSystem.LayoutProps &
  StyledSystem.ColorProps &
  StyledSystem.SpaceProps &
  StyledSystem.BordersProps &
  StyledSystem.BackgroundProps &
  StyledSystem.PositionProps &
  StyledSystem.FlexboxProps &
  StyledSystem.ShadowProps &
  StyledSystem.GridProps &
  StyledSystem.OpacityProps &
  StyledSystem.OverflowProps;

type ModifiedStyledSystemProps = TypographyProps &
  IFontSize &
  ILetterSpacing &
  IFontWeight &
  ILineHeight &
  ICustomConfig;

type BoxHTMLProps = RefAttributes<any>;
